/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, inject } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { DialogService } from 'nebula-ui-kit';
import { SelectDialogComponent } from '../../../select-dialog/select-dialog.component';
import { TercerosService } from './terceros.service';
import { TerceroResponse } from '../../domain/maestro-terceros-modal.models';
import { TranslationService, ACCOUNTING, TREASURY } from '@nebula-shared/data-access';
import { ordersByDelDialogo } from '../../../select-dialog/orden-dialogo';

/** Columna del selector → campo del servidor por el que se ordena (Mantis #392). */
const CAMPOS_ORDEN_TERCERO: Record<string, string> = {
  numeroDocumentoDisplay: 'numeroDocumento',
  nombreDisplay: 'nombreCompleto',
};

@Injectable({ providedIn: 'root' })
export class TerceroModalService {
  readonly dialogService = inject(DialogService);
  readonly tercerosService = inject(TercerosService);
  readonly translation = inject(TranslationService);
  protected t(key: string, params?: any): string {
    return this.translation.translate(key, params);
  }

  truncateText(text: string, maxLength = 20): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  openSelectorThirdPartiesWithOP(
    title: string,
    currentValue?: string,
  ): Observable<TerceroResponse | null> {
    const columns = [
      {
        key: 'numeroDocumentoDisplay',
        label: this.t(TREASURY.PARAMETERS.FIELDS.IDENTIFICATION.LABEL),
        sortable: true,
        width: '40%',
      },
      {
        key: 'nombreDisplay',
        label: this.t(ACCOUNTING.ENTRY_DEFERRED.DEFERRED_MONTH_TABLE.COLUMN.NAME_OR_BUSINESS_NAME),
        sortable: true,
        width: '60%',
      },
    ];

    const loadDataFn = async (params: any): Promise<{ data: any[]; total: number }> => {
      try {
        const page = params.page - 1;
        const size = params.limit;
        const searchTerm = params.search || '';
        // Antes se mandaba la DIRECCION en el lugar de la columna y el orden no se aplicaba.
        const [orden] = ordersByDelDialogo(params, CAMPOS_ORDEN_TERCERO);
        const response = await lastValueFrom(
          this.tercerosService.getTercerosConOpPaginados(
            page,
            size,
            searchTerm,
            orden?.column,
            orden?.direction,
          ),
        );
        const dataFormateada = response.content.map((item: TerceroResponse) => {
          let nombreMostrar = item.nombreCompleto;

          const esJuridica = item.tipoPersona?.toLowerCase().includes('juridica');
          if (esJuridica && item.razonSocial) {
            nombreMostrar = item.razonSocial;
          }

          return {
            ...item,
            numeroDocumentoDisplay: item.dv
              ? `${item.numeroDocumento}-${item.dv}`
              : item.numeroDocumento,
            nombreDisplay: nombreMostrar,
          };
        });

        return {
          data: dataFormateada,
          total: response.totalElements,
        };
      } catch (error) {
        return { data: [], total: 0 };
      }
    };

    if (currentValue) {
      this.tercerosService.buscarPorDocumento(currentValue).subscribe();
    }

    const dialogRef = this.dialogService.open(SelectDialogComponent, {
      data: {
        title: title,
        columns: columns,
        multiSelect: false,
        searchPlaceholder: this.t(TREASURY.COMMON.MODALS.SEARCH.THIRD_PARTY.PLACEHOLDER),
        loadDataFn: loadDataFn,
        itemIdKey: 'id',
        selectedItems: [],
        displayFn: (item: any) => {
          const numeroDocumento = item.numeroDocumentoDisplay || item.numeroDocumento;
          const nombre = item.nombreDisplay || item.nombreCompleto || item.razonSocial;
          const nombreTruncado = this.truncateText(nombre, 30);
          return `${numeroDocumento} - ${nombreTruncado}`;
        },
      },
      size: 'lg',
      width: '800px',
    });

    return dialogRef.afterClosed().pipe(
      map((selected: TerceroResponse[]) => {
        return selected && selected.length > 0 ? selected[0] : null;
      }),
    );
  }
}
